import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  Injector,
  OnInit,
  PLATFORM_ID,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MarketingWebsiteSeoService } from '@core/seo/marketing-website-seo.service';
import { PUBLIC_BRAND_LOGO_SRC, PUBLIC_BRAND_WORDMARK } from '@core/branding/public-brand';
import {
  buildWebsiteMarketingSeoConfig,
  buildWebsitePricingFaqEntries,
  WEBSITE_PRICING_FAQ_KEYS,
} from '../../../utils/website-marketing-seo.utils';
import { WebsiteMarketingPathService } from '../../../services/website-marketing-path.service';
import { WebsiteMediaSlotComponent } from '../../components/website-media-slot/website-media-slot.component';
import { WebsitePublishedPlansService } from '../../../services/website-published-plans.service';
import { WebsitePublishedPlan } from '../../../models/website-published-plan.model';
import {
  fallbackWebsitePublishedPlans,
  formatWebsitePlanPrice,
  websitePaidTrialDays,
  websitePlanFeatureKeys,
} from '../../../utils/website-published-plan.utils';
import { readWebsiteClaim } from '../../../utils/website-claim.storage';
import {
  websiteLoginHref,
  websiteSignupHrefForFree,
  websiteSignupHrefForPlan,
} from '../../../utils/website-auth-urls';

@Component({
  selector: 'app-website-pricing-page',
  standalone: true,
  imports: [CommonModule, RouterModule, TranslateModule, WebsiteMediaSlotComponent],
  templateUrl: './website-pricing-page.component.html',
  styleUrls: ['./website-pricing-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WebsitePricingPageComponent implements OnInit {
  private readonly marketingSeo = inject(MarketingWebsiteSeoService);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly injector = inject(Injector);
  private readonly router = inject(Router);
  readonly mktPath = inject(WebsiteMarketingPathService);
  private readonly publishedPlans = inject(WebsitePublishedPlansService);

  private readonly trackRef = viewChild<ElementRef<HTMLElement>>('plansTrack');

  readonly brandLogoSrc = PUBLIC_BRAND_LOGO_SRC;
  readonly brandWordmark = PUBLIC_BRAND_WORDMARK;
  readonly loginUrl = websiteLoginHref();

  readonly canScrollPrev = signal(false);
  readonly canScrollNext = signal(false);

  readonly trustKeys = ['trial', 'cancel', 'support', 'colombia'] as const;
  readonly includeKeys = ['page', 'updates', 'support', 'grow'] as const;
  readonly faqKeys = WEBSITE_PRICING_FAQ_KEYS;

  readonly plans = signal<WebsitePublishedPlan[]>(fallbackWebsitePublishedPlans());
  readonly paidTrialDays = computed(() => websitePaidTrialDays(this.plans()));
  readonly planIds = computed(() => this.plans().map((plan) => plan.code));
  readonly compareRows = [
    'page',
    'dashboard',
    'catalog',
    'productDetails',
    'store',
    'orders',
    'serviceRequests',
    'pos',
    'payments',
    'customers',
    'finance',
    'cashCounter',
    'inventory',
    'purchases',
    'payroll',
    'tips',
    'credits',
    'reports',
    'team',
    'roles',
    'locations',
    'addons',
  ] as const;
  readonly extraRows = computed(() => this.planIds().filter((id) => id !== 'link'));

  constructor() {
    afterNextRender(
      () => {
        this.syncCarouselNav();
        this.scrollFeaturedIntoView();
        const el = this.trackRef()?.nativeElement;
        if (!el) {
          return;
        }
        const onScroll = () => this.syncCarouselNav();
        el.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onScroll, { passive: true });
        this.destroyRef.onDestroy(() => {
          el.removeEventListener('scroll', onScroll);
          window.removeEventListener('resize', onScroll);
        });
      },
      { injector: this.injector }
    );
  }

  ngOnInit(): void {
    this.applySeo();
    this.destroyRef.onDestroy(() => this.marketingSeo.clear());
    this.translate.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.applySeo());
    this.publishedPlans
      .list()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((rows) => {
        if (rows.length) {
          this.plans.set(rows);
          this.applySeo();
        }
      });
  }

  get registerUrl(): string {
    if (!isPlatformBrowser(this.platformId)) {
      return websiteSignupHrefForFree();
    }
    const claim = readWebsiteClaim();
    return websiteSignupHrefForFree(
      claim?.name?.trim() && claim.slug
        ? { name: claim.name, slug: claim.slug, bio: claim.bio, logoDataUrl: claim.logoDataUrl }
        : undefined
    );
  }

  planPrice(plan: WebsitePublishedPlan): string {
    return formatWebsitePlanPrice(
      plan.priceMonthlyCents,
      plan.currency,
      this.translate.instant('website.plans.link.price')
    );
  }

  choosePlan(plan: WebsitePublishedPlan, event?: Event): void {
    event?.preventDefault();
    if (plan.salesLed) {
      void this.router.navigate(this.mktPath.home(), { fragment: 'contacto' });
      return;
    }
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    globalThis.location.assign(websiteSignupHrefForPlan(plan.code));
  }

  cellKey(planId: string, row: string): string {
    return `website.pricing.compare.${row}.${planId}`;
  }

  planFeatureKeys(planId: string): string[] {
    return websitePlanFeatureKeys(planId);
  }

  scrollPlans(direction: -1 | 1): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    const el = this.trackRef()?.nativeElement;
    if (!el) {
      return;
    }
    const card = el.querySelector<HTMLElement>('.pricing-card');
    const delta = (card?.offsetWidth ?? 320) + 20;
    el.scrollBy({ left: direction * delta, behavior: 'smooth' });
  }

  scrollToCompare(event: Event): void {
    event.preventDefault();
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    document.getElementById('comparar')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  private scrollFeaturedIntoView(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    const el = this.trackRef()?.nativeElement;
    const featured = el?.querySelector<HTMLElement>('.pricing-card--hot');
    if (!el || !featured) {
      return;
    }
    const left = featured.offsetLeft - (el.clientWidth - featured.offsetWidth) / 2;
    el.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
    this.syncCarouselNav();
  }

  private syncCarouselNav(): void {
    const el = this.trackRef()?.nativeElement;
    if (!el) {
      this.canScrollPrev.set(false);
      this.canScrollNext.set(false);
      return;
    }
    const max = el.scrollWidth - el.clientWidth;
    this.canScrollPrev.set(el.scrollLeft > 8);
    this.canScrollNext.set(el.scrollLeft < max - 8);
  }

  private applySeo(): void {
    this.marketingSeo.apply(
      buildWebsiteMarketingSeoConfig(
        'pricing',
        {
          docTitle: this.translate.instant('website.pricing.docTitle'),
          metaDescription: this.translate.instant('website.pricing.metaDescription'),
          heroTitle: this.translate.instant('website.pricing.title'),
          heroSub: this.translate.instant('website.pricing.sub'),
          faq: buildWebsitePricingFaqEntries(WEBSITE_PRICING_FAQ_KEYS, (fullKey: string) =>
            this.translate.instant(fullKey, { days: this.paidTrialDays() })
          ),
        },
        typeof window !== 'undefined' ? window.location.origin : undefined
      )
    );
  }
}
